import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import serviceToken from '../../services/requestToken';
import { PermissionName, PERMISSIONS } from '../../components/permission/permissionsConstants';

const RETAILER_BASE_API = process.env.REACT_APP_RETAILER_BASE_API;

export interface UserPermissions {
  permissions: PermissionName[];
  isSubAccount: boolean;
}

interface PermissionStore {
  // State
  userPermissions: UserPermissions | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchUserPermissions: (options?: { silent?: boolean }) => Promise<void>;
  setUserPermissions: (permissions: UserPermissions) => void;
  clearPermissions: () => void;
  hasPermission: (permission: PermissionName | PermissionName[]) => boolean;
  hasAnyPermission: (permissions: PermissionName[]) => boolean;
  hasAllPermissions: (permissions: PermissionName[]) => boolean;
}

const usePermissionStore = create<PermissionStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        userPermissions: null,
        isLoading: false,
        error: null,

        fetchUserPermissions: async (options = {}) => {
          const { isLoading } = get();
          const { silent = false } = options;
          
          if (isLoading) {
            console.log('⏳ Permission fetch already in progress, skipping...');
            return;
          }
          
          if (silent) {
            set({ error: null });
          } else {
            set({ isLoading: true, error: null });
          }

          try {
            const response = await serviceToken.get(
              RETAILER_BASE_API + `retailers/permissions`
            );
            
            const responseData = response.data;
            
            if (responseData.success && responseData.data) {
              const permissions: PermissionName[] = (responseData.data.permissions || []).map(
                (permissionName: string) => permissionName as PermissionName
              );
              
              set({
                userPermissions: {
                  permissions: permissions,
                  isSubAccount: Boolean(responseData.data.is_sub_account)
                },
                isLoading: false
              });
              
              console.log('✅ Permissions loaded successfully:', permissions);
            } else {
              set({
                error: responseData.message || 'Failed to fetch permissions',
                isLoading: false
              });
            }
          } catch (error: any) {
            console.error('❌ Failed to fetch user permissions:', error);
            set({
              error: error.message || 'Failed to fetch permissions',
              isLoading: false
            });
          }
        },

        setUserPermissions: (permissions: UserPermissions) => {
          set({ userPermissions: permissions });
        },

        clearPermissions: () => {
          set({ userPermissions: null, error: null });
          try {
            localStorage.removeItem('permission-storage');
          } catch (e) {
            console.error('Failed to clear permission storage:', e);
          }
        },

        hasPermission: (permission: PermissionName | PermissionName[]) => {
          const { userPermissions } = get();
          if (!userPermissions) {
            return false;
          }

          const hasAllManage = userPermissions.permissions.includes(PERMISSIONS.ALL_MANAGE);
          if (hasAllManage) {
            return true;
          }

          if (Array.isArray(permission)) {
            return permission.every((p) =>
              userPermissions.permissions.includes(p)
            );
          }

          return userPermissions.permissions.includes(permission);
        },


        hasAnyPermission: (permissions: PermissionName[]) => {
          const { userPermissions } = get();

          if (!userPermissions) {
            return false;
          }

          const hasAllManage = userPermissions.permissions.includes(PERMISSIONS.ALL_MANAGE);
          if (hasAllManage) {
            return true;
          }

          return permissions.some((p) =>
            userPermissions.permissions.includes(p)
          );
        },

        hasAllPermissions: (permissions: PermissionName[]) => {
          const { userPermissions } = get();

          if (!userPermissions) {
            return false;
          }

          const hasAllManage = userPermissions.permissions.includes(PERMISSIONS.ALL_MANAGE);
          if (hasAllManage) {
            return true;
          }

          return permissions.every((p) =>
            userPermissions.permissions.includes(p)
          );
        }
      }),
      {
        name: 'permission-storage',
        partialize: (state) => ({
          userPermissions: state.userPermissions
        })
      }
    ),
    { name: 'PermissionStore' }
  )
);

export default usePermissionStore;

